import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, PatternValidator, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import intlTelInput, { Iti } from 'intl-tel-input';
import { es, pt } from 'intl-tel-input/i18n';
import { DatePickerType, ISTabLayout } from 'is-lib';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, debounceTime, distinctUntilChanged, finalize, forkJoin, Observable, Subject, take, takeUntil, takeWhile } from 'rxjs';
import { ClientPortalUser, CreateClient } from 'src/app/models/Client';
import { ServerResponse } from 'src/app/models/ServerResponse';
import { SnackBarType } from 'src/app/models/Snackbar';
import { UserProfile, UserProfileEdit } from 'src/app/models/UserProfile';
import { SnackBarService } from 'src/app/services/snackbar.service';
import { UsersService } from 'src/app/services/users.service';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit, OnDestroy {
    //#region Variables
    // Ends the translation stream subscription, which never completes on its own
    private readonly _destroy$ = new Subject<void>();
    protected _userRequestCompletedFlag: boolean = false;
    protected _alteredFormFlag: boolean = false;

    tabLayout$!: BehaviorSubject<ISTabLayout>; //object with tabs
    
    selectedTab: number = 0;
    userData!: UserProfile;

    userForm = new FormGroup({
        firstName: new FormControl<string>('', Validators.required),
        lastName: new FormControl<string>('', Validators.required),
        birthDate: new FormControl<Date | undefined>(undefined),
        tin: new FormControl<string>('', Validators.compose([Validators.required, Validators.minLength(9), Validators.maxLength(9)])),
        contactNumber: new FormControl<string>('', Validators.compose([Validators.minLength(9), Validators.maxLength(9)])),
        avatar: new FormControl<string | undefined>(undefined)
    })

    cellphone: Iti | null = null;

    contractsCount = 0;
    supplyPointsCount = 0;

    dateType = DatePickerType.date;
    dateLimits = [null, new Date()]

    file?: File;
    avatarSafeUrl?: SafeHtml
    //#endregion

    //#region Functions
    
    @ViewChild('phone') set phoneInputSetup(phoneInput: ElementRef) {
        if (phoneInput != null) {
            if (this.cellphone == null) {
                this.cellphone = intlTelInput(
                    phoneInput.nativeElement,
                    {
                        i18n: this._usersService.userCountryCode! == 'PT' ? pt : es,
                        initialCountry: this._usersService.userCountryCode!,
                        separateDialCode: true,
                        strictMode: true,
                        placeholderNumberType: 'FIXED_LINE_OR_MOBILE',
                        validationNumberTypes: ['FIXED_LINE_OR_MOBILE'],
                        formatAsYouType: true,
                        formatOnDisplay: true
                    }
                )

                this.cellphone?.setNumber(this.userData?.contactNumber ?? '')
            }
            
        } else {
            this.cellphone?.destroy();
            this.cellphone = null;
            
        }

    }

    constructor(
        private _usersService: UsersService,
        private _router: Router,
        private _snackbar: SnackBarService,
        private _sanitizer: DomSanitizer,
        private _spinnerService: NgxSpinnerService,
        private _translateService: TranslateService
    ) {}

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();

        // The phone input's ViewChild setter isn't called on destroy, and intl-tel-input keeps its instances and listeners until destroy()
        this.cellphone?.destroy();
        this.cellphone = null;
    }

    ngOnInit(): void { 
        let requests = {
            user: this._usersService.getCurrentUser(this._usersService.userCountryCode!),
            userClaims: this._usersService.userData$.pipe(take(1))
        };

        forkJoin(requests).pipe(
            finalize(() => {
                this._userRequestCompletedFlag = true;
                this.fillFormWithAPIData();
            })
        ).subscribe( {
            next: 
                (response) => {
                    if (response.user.data != null) {
                        this.userData = response.user.data;
                    }
                },
            error:
                (erorr: HttpErrorResponse) => {
                    if (erorr.status == 404) {
                        let dataFromClaims = this._usersService.userData;
                        
                        this.userData = new UserProfile({
                            email: dataFromClaims!.email,
                            firstName: dataFromClaims!.firstName,
                            lastName: dataFromClaims!.lastName,
                            countryId: dataFromClaims!.countryId
                        })
                        
                    } else {
                        this._router.navigate(['/']);
                    }
                    
                }
        });
        this._translateService.stream(['PERSONAL_DATA', 'ACCESS_DATA']).pipe(takeUntil(this._destroy$)).subscribe((translations: any) => {
            let selectedMenu = 0;
            this.tabLayout$ = new BehaviorSubject<ISTabLayout>({
            selectedMenu: selectedMenu,
            tabItems: [
                { id: 0, label: translations.PERSONAL_DATA },
                { id: 1, label: translations.ACCESS_DATA },
            ],
            verticalLayout: false
        });
        });
    }
    
    fillFormWithAPIData() {
        this.userForm.patchValue({
            firstName: this.userData.firstName,
            lastName: this.userData.lastName,
            birthDate: this.userData.birthDate,
            contactNumber: '',
            tin: this.userData.tin ?? null,
            avatar: null
        });

        if (this.userData.avatar) {
            let base64String = 'data:image/jpeg;base64,' + this.userData.avatar
            this.userForm.controls.avatar.setValue(base64String);
            this.avatarSafeUrl = this._sanitizer.bypassSecurityTrustUrl(base64String);
        }

        if (this.cellphone) {
            this.cellphone.setNumber(this.userData?.contactNumber ?? '')
        }
        
        this.userForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(500)).subscribe((newFormValue) => {
            this._alteredFormFlag = this.formDataWasAlteredCheck();
        })
    }

    handleFileUpload(fileInput: HTMLInputElement) {

        if (fileInput.files != null && fileInput.files.length >= 1) {
            let newFile: File = fileInput.files.item(0)!;
            let fileReader = new FileReader();
            this.file = newFile;

            fileReader.readAsDataURL(newFile);
            fileReader.onload = () => { 
                this.avatarSafeUrl = this._sanitizer.bypassSecurityTrustUrl(fileReader.result!.toString());

                let imageRegex = new RegExp('data:.+?;base64,', 'g')
                this.userForm.controls.avatar.setValue(fileReader.result!.toString().replace(imageRegex, ''));
            }

            fileReader.onerror = fileReader.onabort = () => {
                this.userForm.controls.avatar.setValue(null);
                this.file = undefined;
                this.avatarSafeUrl = undefined;
            }

        } else {
            this.userForm.controls.avatar.setValue(null);
            this.avatarSafeUrl = undefined;
        }
    }

    submitProfile() {
        if (this.userData.id == null) {
            this.submitProfileCreation();
        } else {
            this.submitProfileEdit();
        }
    }

    submitProfileCreation() {
        if (this.userForm.valid) {
            let clientCreationData: CreateClient = 
            new CreateClient(
                this.userData.countryId,
                this.userData.email,
                this.userForm.controls.firstName.value!,
                this.userForm.controls.lastName.value!,
                '______'
            )
            this._usersService.createUser(clientCreationData).subscribe({
                next: (sr: ServerResponse<number>) => {
                    if (sr.data != null) {
                        this.userData.id = sr.data;

                        this.submitProfileEdit(true);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    this._snackbar.callSnackBar(error.message, SnackBarType.ERROR);
                }
            })
        }
    }

    submitProfileEdit(skipFormValidationSteps: boolean = false) {
        if (this.userForm.valid || skipFormValidationSteps) {
            let requests: {[key: string]: Observable<ServerResponse<any>>} = {};
            
            
            if (this.formDataWasAlteredCheck(false) || skipFormValidationSteps) {
                
                let formSubmition: UserProfileEdit = {
                    contactNumber: this.userForm.controls.contactNumber.value!,
                    firstName: this.userForm.controls.firstName.value!,
                    lastName: this.userForm.controls.lastName.value!,
                    tin: this.userForm.controls.tin.value!,
                    birthDate: this.userForm.controls.birthDate.value!,
                    id: this.userData.id!
                }

                requests['user'] = this._usersService.editCurrentUser(this._usersService.userCountryCode!, formSubmition)
            } 

            let avatarRequestData = this.getAvatarDataForRequest();
            if (avatarRequestData != null) {
                requests['avatar'] = this._usersService.editCurrentUserAvatar(this._usersService.userCountryCode!, avatarRequestData)
            }

            forkJoin(requests).subscribe({
                next: 
                    (response: {[key: string]: ServerResponse<any>}) => {
                        let success = false;
                        let userData = this._usersService.userData!;

                        if (response['user'] != null) {
                            if (response['user'].data != null) {
                                success = true;

                                if (userData) {
                                    userData.birthdate = response['user'].data.birthDate
                                    userData.contactNumber = response['user'].data.contactNumber
                                    userData.email = response['user'].data.email
                                    userData.firstName = response['user'].data.firstName
                                    userData.lastName = response['user'].data.lastName
                                    userData.tin = response['user'].data.tin
                                    userData.entityGuid = response['user'].data.id
                                }
                                
                            }
                        }

                        if (response['avatar'] != null) {
                            if (response['avatar'].data != null) {
                                success = true;
                                userData.avatar = this.userForm.controls.avatar.value ?? undefined
                            }
                        }

                        if (success) {                                
                            this._usersService.userData = userData;

                            this._snackbar.callSnackBar('USER_DATA_ALTERED_SUCCESS', SnackBarType.SUCCESS);
                            this._router.navigate(['/']);
                        }
                        
                    },
                error:
                    (err: HttpErrorResponse) => {
                        this._snackbar.callSnackBar(err.message, SnackBarType.ERROR);
                    }
            })
        } else {
            this._snackbar.callSnackBar( 'FORM_HAS_ERRORS', SnackBarType.WARNING )
        }
    }

    private getAvatarDataForRequest(): FormData | null {
        if (this.userForm.controls.avatar.value != null && this.userForm.controls.avatar.value.split('base64,').at(1) != this.userData.avatar) {            
            let formDataAvatarSubmition: FormData | undefined;
            let treatedFileName = this.file?.name.replaceAll(new RegExp('[^A-Za-z0-9.]', 'g'), '_');

            formDataAvatarSubmition = new FormData();
            formDataAvatarSubmition.append('ClientPortalUserId', this.userData.id!.toString());
            formDataAvatarSubmition.append('File', this.file!, treatedFileName);

            return formDataAvatarSubmition;
        }

        return null;
    }

    formDataWasAlteredCheck(checkAvatar: boolean = true): boolean {
        let isDifferent = Object.entries(this.userForm.value).some(([key, value]) => {
            if (!checkAvatar && key == 'avatar') {
                return false;
            }

            let userDataValue = this.userData[key];

            return userDataValue != value;
        })

        return isDifferent;
    }

    onTabChange(selectedTab: number) {
        this.selectedTab = selectedTab;
    }
    //#endregion
    
}
